'use client';

import { Tooltip, TooltipContent, TooltipTrigger } from '@/_components/UIComponents/Tooltip';
import { Skeleton } from '@/_components/UIComponents/Skeleton';
import { Citation, IDataPoolEntry } from '@/types';
import { parseStepPath, getValueAtPath, formatValuePreview } from '@/_utils/citationPath';
import { getDataSourceDisplay } from '@/_utils/dataSourceDisplay';
import { DatabaseIcon, ClockIcon } from './icons';

interface CitationTooltipProps {
	/** The citation data */
	citation: Citation;
	/** Data pool entries for looking up raw data */
	dataPoolEntries?: IDataPoolEntry[];
	/** Callback to trigger lazy loading of message details */
	onRequestDetails?: () => void;
	/** The content that triggers the tooltip */
	children: React.ReactNode;
}

/** Skeleton layout matching the real tooltip structure */
function TooltipSkeleton() {
	return (
		<>
			{/* Header skeleton */}
			<div className='border-border mb-2 flex items-center gap-2 border-b pb-2'>
				<Skeleton className='h-3.5 w-3.5 rounded' />
				<Skeleton className='h-4 w-20 rounded' />
			</div>
			{/* JSON path skeleton */}
			<div className='mb-2'>
				<Skeleton className='h-3 w-full rounded' />
			</div>
			{/* Value preview skeleton */}
			<div className='bg-secondary/50 mb-2 rounded p-2'>
				<Skeleton className='mb-1.5 h-3 w-full rounded' />
				<Skeleton className='h-3 w-2/3 rounded' />
			</div>
			{/* Footer skeleton */}
			<div className='flex items-center gap-1.5'>
				<Skeleton className='h-3 w-3 rounded' />
				<Skeleton className='h-3 w-28 rounded' />
			</div>
		</>
	);
}

/**
 * Citation tooltip component that displays source preview on hover
 * Shows data source, JSON path, value preview, and fetch timestamp
 * When data is not yet loaded, shows skeleton loaders and triggers lazy loading
 */
function CitationTooltip({ citation, dataPoolEntries = [], children, onRequestDetails }: CitationTooltipProps) {
	const { stepIndex } = parseStepPath(citation.jsonPath);
	const entry = dataPoolEntries.find((e) => e.stepIndex === stepIndex);

	const handleOpenChange = (open: boolean) => {
		if (open && !entry && onRequestDetails) {
			onRequestDetails();
		}
	};

	const value = entry ? getValueAtPath(dataPoolEntries, citation.jsonPath) : undefined;
	const sourceDisplay = entry ? getDataSourceDisplay(entry.dataSource) : null;
	const fetchTime = entry ? new Date(entry.fetchedAt).toLocaleString() : null;

	return (
		<Tooltip onOpenChange={handleOpenChange}>
			<TooltipTrigger asChild>{children}</TooltipTrigger>
			<TooltipContent
				side='top'
				sideOffset={8}
				hideArrow
				className='border-border bg-popover max-w-[320px] rounded-lg border p-3 shadow-xl'
			>
				{entry && sourceDisplay ? (
					<>
						{/* Header with data source */}
						<div className='border-border mb-2 flex items-center gap-2 border-b pb-2'>
							<span className='text-muted-foreground'>{DatabaseIcon}</span>
							<span className={`rounded px-1.5 py-0.5 text-xs font-medium uppercase ${sourceDisplay.colorClass}`}>
								{sourceDisplay.name}
							</span>
						</div>

						{/* JSON Path */}
						<div className='mb-2'>
							<code className='block text-xs break-all text-sky-400'>{citation.jsonPath}</code>
						</div>

						{/* Value Preview */}
						<div className='bg-secondary/50 mb-2 rounded p-2'>
							<p className='text-foreground text-sm break-words'>{formatValuePreview(value)}</p>
						</div>

						{/* Footer with timestamp */}
						<div className='text-muted-foreground flex items-center gap-1.5 text-xs'>
							{ClockIcon}
							<span>Fetched at {fetchTime}</span>
						</div>
					</>
				) : (
					<TooltipSkeleton />
				)}
			</TooltipContent>
		</Tooltip>
	);
}

export default CitationTooltip;
export type { CitationTooltipProps };
